// Date utilities to handle dates without timezone conversion

/**
 * Format a date string (YYYY-MM-DD) to Brazilian format (DD/MM/YYYY)
 * WITHOUT timezone conversion
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @returns {string} Date string in DD/MM/YYYY format
 */
export const formatDateBrazilian = (dateString) => {
  if (!dateString) return '-';

  // Handle if it's already a Date object (shouldn't happen, but defensive)
  if (dateString instanceof Date) {
    const year = dateString.getFullYear();
    const month = String(dateString.getMonth() + 1).padStart(2, '0');
    const day = String(dateString.getDate()).padStart(2, '0');
    return `${day}/${month}/${year}`;
  }

  // Parse the date string directly without timezone conversion
  const match = String(dateString).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return dateString; // Return as-is if not in expected format

  const [_, year, month, day] = match;
  return `${day}/${month}/${year}`;
};

/**
 * Compare two date strings without timezone conversion
 * @param {string} date1 - Date string in YYYY-MM-DD format
 * @param {string} date2 - Date string in YYYY-MM-DD format
 * @returns {number} -1 if date1 < date2, 0 if equal, 1 if date1 > date2
 */
export const compareDates = (date1, date2) => {
  // Direct string comparison works for YYYY-MM-DD format
  if (date1 < date2) return -1;
  if (date1 > date2) return 1;
  return 0;
};

/**
 * Check if a date is before today without timezone conversion
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @returns {boolean} True if date is before today
 */
export const isBeforeToday = (dateString) => {
  if (!dateString) return false;

  // Get today's date in YYYY-MM-DD format without timezone
  const today = new Date();
  const todayString = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  return dateString < todayString;
};

/**
 * Get today's date in YYYY-MM-DD format
 * @returns {string} Today's date in YYYY-MM-DD format
 */
export const getTodayString = () => {
  const today = new Date();
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
};

/**
 * Parse a date string without timezone conversion (for sorting)
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @returns {object} Object with year, month, day as numbers
 */
export const parseDateString = (dateString) => {
  if (!dateString) return { year: 0, month: 0, day: 0 };

  const match = String(dateString).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return { year: 0, month: 0, day: 0 };

  const [_, year, month, day] = match;
  return {
    year: parseInt(year, 10),
    month: parseInt(month, 10),
    day: parseInt(day, 10)
  };
};

/**
 * Sort date strings without timezone conversion
 * @param {string} a - Date string in YYYY-MM-DD format
 * @param {string} b - Date string in YYYY-MM-DD format
 * @returns {number} Sort order
 */
export const sortDates = (a, b) => {
  // Direct string comparison works for YYYY-MM-DD format
  return a < b ? -1 : a > b ? 1 : 0;
};

/**
 * Calculate the number of days until a given date from today
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @returns {number|null} Number of days until the date (negative if in the past), or null if no date
 */
export const getDaysUntilDate = (dateString) => {
  if (!dateString) return null;

  // Get today's date in YYYY-MM-DD format
  const todayString = getTodayString();

  // Parse both dates
  const targetParts = parseDateString(dateString);
  const todayParts = parseDateString(todayString);

  // Create Date objects at midnight local time
  const targetDate = new Date(targetParts.year, targetParts.month - 1, targetParts.day);
  const todayDate = new Date(todayParts.year, todayParts.month - 1, todayParts.day);

  // Calculate difference in milliseconds and convert to days
  const diffTime = targetDate - todayDate;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return diffDays;
};